import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Grid,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  Link,
  Snackbar,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

import Terminos from './Terminos';
import encabezadoImg from '../src/encabezado.jpg';

// ============ Styled Dialog for "Gracias por registrarse" ============
const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2),
  },
}));

function RegistroComercio() {
  // form state
  const [calle, setCalle] = useState('');
  const [numero, setNumero] = useState('');
  const [cuit, setCuit] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [email, setEmail] = useState('');
  const [contacto, setContacto] = useState('');
  const [web, setWeb] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [socio, setSocio] = useState('');

  // ui state
  const [openDialogOK, setOpenDialogOK] = useState(false);
  const [openDialogError, setOpenDialogError] = useState(false);
  const [openTerminos, setOpenTerminos] = useState(false);
  const [snackbarOk, setSnackbarOk] = useState(false);

  // validation state
  const [cuitError, setCuitError] = useState(false);

  // status flags
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();

  // -------- Helpers --------

  const volver = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Valida CUIT con la misma lógica que ya tenías,
  // pero setea cuitError en vez de showAlert.
  const verificaCUIT = useCallback(
    (valor) => {
      let aux = 0;
      const arreglo = valor.split('');

      for (let index = 0; index < arreglo.length - 1; index++) {
        const factor = (() => {
          switch (index) {
            case 0:
              return 5;
            case 1:
              return 4;
            case 2:
              return 3;
            case 3:
              return 2;
            case 4:
              return 7;
            case 5:
              return 6;
            case 6:
              return 5;
            case 7:
              return 4;
            case 8:
              return 3;
            case 9:
              return 2;
            default:
              return 1;
          }
        })();

        aux += parseInt(arreglo[index], 10) * factor;
      }

      aux = aux % 11;

      // misma condición que tu código original:
      const valido =
        !(
          ((11 - aux !== Number(arreglo[10]) && aux !== 0) ||
            arreglo.length !== 11)
        );

      setCuitError(!valido);
    },
    [setCuitError]
  );

  // -------- Submit handler --------
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Evitar doble submit
    if (submitting) return;
    setSubmitting(true);

    const postData = {
      nombredefantasia: '', // futuro, lo usás en sucursal
      cuit: cuit,
      razonsocial: razonSocial,
      calle: calle,
      numero: numero,
      email: email,
      contacto: contacto,
      socioucip: socio,
      aprobacion: false,
      fecha_alta: new Date(),
      fecha_aprobacion: new Date(),
      detalle_aprobacion: '',
      idrubro: -1,
      web: web,
      whatsapp: whatsapp,
    };

    try {
      const baseURL = process.env.REACT_APP_SERVER_PATH + 'addComercio';
      const res = await fetch(baseURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': 'token-value', // TODO: reemplazar token real
        },
        body: JSON.stringify(postData),
      });

      console.log('status:', res.status);

      if (res.status === 200 || res.status === 204) {
        // limpiar form
        setCalle('');
        setNumero('');
        setCuit('');
        setRazonSocial('');
        setEmail('');
        setContacto('');
        setSocio('');
        setWeb('');
        setWhatsapp('');

        // feedback al usuario
        setOpenDialogOK(true);
        setSnackbarOk(true);
      } else if (res.status === 505) {
        setOpenDialogError(true);
      } else {
        // cualquier otro status lo tratamos como error genérico
        setOpenDialogError(true);
      }
    } catch (err) {
      console.error(err);
      setOpenDialogError(true);
    } finally {
      setSubmitting(false);
    }
  };

  // -------- useEffect (carga inicial si hiciera falta) --------
  useEffect(() => {
    // acá en tu código original ibas a cargar combos como Rubros o CentrosComerciales
    // lo dejo en blanco por ahora
  }, []);

  // -------- UI Dialog handlers --------
  const handleCloseOK = () => {
    setOpenDialogOK(false);
    volver();
  };

  const handleCloseError = () => {
    setOpenDialogError(false);
  };

  const handleCloseTerminos = () => {
    setOpenTerminos(false);
  };

  return (
    <>
      {/* Dialog éxito */}
      <BootstrapDialog open={openDialogOK} onClose={handleCloseOK}>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Gracias por registrarse
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseOK}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Typography gutterBottom variant="body2" color="text.secondary">
            Ya puede ingresar con sus credenciales para completar sus datos e
            imprimir el código QR.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            autoFocus
            onClick={handleCloseOK}
          >
            Cerrar
          </Button>
        </DialogActions>
      </BootstrapDialog>

      {/* Dialog términos */}
      <Dialog open={openTerminos} onClose={handleCloseTerminos} maxWidth="md" fullWidth>
        <DialogTitle>Términos y Condiciones</DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div">
            <Terminos />
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseTerminos} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog error */}
      <Dialog open={openDialogError} onClose={handleCloseError}>
        <DialogTitle>Error en el registro</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Ya existe un comercio con ese CUIT, o hubo un problema con el alta.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseError} variant="contained" color="error">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de éxito corto */}
      <Snackbar
        open={snackbarOk}
        autoHideDuration={4000}
        onClose={() => setSnackbarOk(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnackbarOk(false)}
        >
          <AlertTitle>Registro Correcto</AlertTitle>
          Gracias por registrarse. Ya puede ingresar con sus credenciales.
        </Alert>
      </Snackbar>

      <Container component="main" maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Encabezado gráfico */}
        <Box
          sx={{
            mb: 3,
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
          }}
        >
          <Box
            component="img"
            src={encabezadoImg}
            alt="Encabezado"
            sx={{
              display: 'block',
              width: '100%',
              height: 'auto',
              objectFit: 'cover',
            }}
          />
        </Box>

        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
          }}
        >
          <CardHeader
            title="Registro de Comercio"
            subheader="Completá tus datos para generar tu acceso"
            sx={{
              textAlign: 'center',
              pb: 0,
              '& .MuiCardHeader-title': {
                fontWeight: 600,
                fontSize: '1.25rem',
              },
              '& .MuiCardHeader-subheader': {
                fontSize: '0.9rem',
              },
            }}
          />

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            autoComplete="off"
          >
            <CardContent sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                {/* Calle */}
                <Grid item xs={12} sm={8}>
                  <TextField
                    required
                    fullWidth
                    id="calle"
                    label="Calle"
                    value={calle}
                    onChange={(e) => setCalle(e.target.value)}
                    autoComplete="street-address"
                  />
                </Grid>

                {/* Número */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    required
                    fullWidth
                    id="numero"
                    label="Número"
                    type="number"
                    value={numero}
                    onChange={(e) => setNumero(e.target.value)}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                {/* CUIT */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    required
                    fullWidth
                    id="cuit"
                    label="CUIT"
                    type="number"
                    value={cuit}
                    onChange={(e) => setCuit(e.target.value)}
                    onBlur={() => verificaCUIT(cuit)}
                    error={cuitError}
                    helperText={
                      cuitError ? 'CUIT inválido, verifícalo' : ' '
                    }
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                  />
                </Grid>

                {/* Razón social */}
                <Grid item xs={12} sm={8}>
                  <TextField
                    required
                    fullWidth
                    id="razonSocial"
                    label="Razón Social"
                    value={razonSocial}
                    onChange={(e) => setRazonSocial(e.target.value)}
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12} sm={7}>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    label="Correo electrónico"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </Grid>

                {/* Contacto */}
                <Grid item xs={12} sm={5}>
                  <TextField
                    required
                    fullWidth
                    id="contacto"
                    label="Nombre de contacto"
                    value={contacto}
                    onChange={(e) => setContacto(e.target.value)}
                  />
                </Grid>

                {/* Web */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="web"
                    label="Sitio web"
                    placeholder="https://tudominio.com"
                    value={web}
                    onChange={(e) => setWeb(e.target.value)}
                  />
                </Grid>

                {/* WhatsApp */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="whatsapp"
                    label="WhatsApp"
                    placeholder="549223..."
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                  />
                </Grid>

                {/* ¿Es socio? */}
                <Grid item xs={12} sm={6}>
                  <FormControl required fullWidth>
                    <InputLabel id="socio-label">¿Es socio?</InputLabel>
                    <Select
                      labelId="socio-label"
                      id="socio"
                      value={socio}
                      label="¿Es socio?"
                      onChange={(e) => setSocio(e.target.value)}
                    >
                      <MenuItem value={true}>Sí</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Términos */}
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    flexWrap: 'wrap',
                    mt: 1,
                  }}
                >
                  <Checkbox required />
                  <Typography variant="body2" color="text.secondary">
                    Acepto los{' '}
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      sx={{ fontWeight: 500 }}
                      onClick={() => setOpenTerminos(true)}
                    >
                      Términos y Condiciones
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>

            <CardActions
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'flex-end',
                gap: 2,
                px: 3,
                pb: 3,
              }}
            >
              <Button
                fullWidth
                sx={{ minWidth: 140 }}
                variant="contained"
                color="secondary"
                disabled={submitting}
                onClick={volver}
              >
                Cancelar
              </Button>

              <Button
                fullWidth
                sx={{ minWidth: 140 }}
                variant="contained"
                color="primary"
                type="submit"
                disabled={submitting || cuitError}
              >
                {submitting ? 'Enviando...' : 'Registrar'}
              </Button>
            </CardActions>
          </Box>
        </Card>
      </Container>
    </>
  );
}

export default RegistroComercio;
